import React from 'react'
import { useParams } from 'react-router-dom'

import ProfileSection from 'app/common/pixel-menu/_comp/ProfileSection'
import {
  DcBreadcrumbs,
  DcFlexBox,
  PixelSpinner,
  PixelPageContainer,
  PixelTable,
  Section,
  SectionContent,
} from 'pixel-base'
import { getProductBom, type ProductBomItem } from 'utils/api-routes-planning'

const breadcrumbs = [
  {
    name: 'Production Planning',
    route: '/planning',
  },
  {
    name: 'Lead Time & MOQ Overview',
    route: '/planning/lead-time',
  },
  {
    name: 'Product BOM Detail',
    active: true,
  },
]

const columns = [
  {
    dataKey: 'component_sku',
    name: 'Component SKU',
    sortable: true,
    selector: (row: ProductBomItem) => row.component_sku || '--',
  },
  {
    dataKey: 'component_name',
    name: 'Component Name',
    sortable: true,
    selector: (row: ProductBomItem) => row.component_name || '--',
  },
  {
    dataKey: 'quantity',
    name: 'Required Qty',
    sortable: true,
    selector: (row: ProductBomItem) =>
      typeof row.quantity === 'number' ? row.quantity : '--',
  },
  {
    dataKey: 'uom',
    name: 'UOM',
    sortable: true,
    selector: (row: ProductBomItem) => row.uom || '--',
  },
  {
    dataKey: 'lead_time',
    name: 'Lead Time (days)',
    sortable: true,
    selector: (row: ProductBomItem) =>
      typeof row.lead_time === 'number' ? row.lead_time : '--',
  },
  {
    dataKey: 'moq',
    name: 'MOQ',
    sortable: true,
    selector: (row: ProductBomItem) =>
      typeof row.moq === 'number' ? row.moq : '--',
  },
]

const LeadTimeBomDetail: React.FC = () => {
  const { productId } = useParams<{ productId: string }>()
  const [items, setItems] = React.useState<ProductBomItem[]>([])
  const [isLoading, setIsLoading] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  React.useEffect(() => {
    if (!productId) return

    setIsLoading(true)
    setError(null)

    const subscription = getProductBom(productId).subscribe({
      next: (res) => {
        setItems(res.response?.data || [])
        if (res.response?.error) {
          setError(res.response.error)
        }
        setIsLoading(false)
      },
      error: () => {
        setError('Failed to load product BOM data')
        setItems([])
        setIsLoading(false)
      },
    })

    return () => {
      subscription.unsubscribe()
    }
  }, [productId])

  return (
    <>
      <DcFlexBox
        height="60px"
        style={{
          fontSize: '16px',
          padding: '8px 0px',
          borderBottom: '1px solid #e5e5e5',
        }}
      >
        <DcFlexBox
          justifyContent="space-between"
          alignItems="center"
          padding=".5rem 20px"
          width="100%"
        >
          <DcBreadcrumbs
            className="breadcrumbs"
            fontSize="16px"
            data={breadcrumbs}
          />
          <ProfileSection />
        </DcFlexBox>
      </DcFlexBox>

      <PixelPageContainer padding="0px" height="calc(100vh - 61px)">
        <Section style={{ padding: '0px 20px 20px' }}>
          <SectionContent>
            {isLoading && <PixelSpinner size="20px" isShow={isLoading} />}

            {!isLoading && !error && items.length > 0 && (
              <PixelTable
                columns={columns}
                data={items}
                isSearchable={true}
                fixedHeader
                fixedHeaderScrollHeight="calc(100vh - 220px)"
                pagination={items.length > 10}
                paginationPerPage={10}
                progressPending={isLoading}
              />
            )}
          </SectionContent>
        </Section>
      </PixelPageContainer>
    </>
  )
}

export default LeadTimeBomDetail
